/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React from 'react';
import { useDispatch } from 'react-redux';
import { css, StyleSheet } from 'aphrodite';
import { mixins, colors, ActionButton } from '@blend/consumer-components';
import { VideoInputDevice } from '../../form/TaskInputs/VideoInputDevice/VideoInputDevice';
import { AudioInputDevice } from '../../form/TaskInputs/AudioInputDevice/AudioInputDevice';
import { AudioOutputDevice } from '../../form/TaskInputs/AudioOutputDevice/AudioOutputDevice';
import { LocalRoomSetup } from '../../form/LocalRoomSetup/LocalRoomSetup';
import { useRoom } from '../../../hooks/useRoom';
import { mediaBreakpoints } from '../../../common/responsive';
import { useRootSelector } from '../../../hooks/useRootSelector';
import { updateExitModalStatus } from '../../../actions/signingRoomActions';
import { useVideoContext } from '../../../contexts/videoProvider/videoContext';
import { getVideoVendorApiKey } from '@utils/videoVendorStorageUtils';
import { ClosedCaptionsToggle } from '@components/videoProvider/zoom/ClosedCaptionsToggle/ClosedCaptionsToggle';
import { noop } from '@utils/lodash';

export namespace DeviceSetting {
  export interface Props {}
}

const styles = StyleSheet.create({
  deviceSetting: {
    [mediaBreakpoints.mobile]: {
      position: 'relative',
      flexGrow: 1,
      flexShrink: 1,
    },
  },
  wrapper: {
    ...mixins.padding(0, 8),
    [mediaBreakpoints.mobile]: {
      ...mixins.fillContainer(),
      overflowY: 'scroll',
      ...mixins.padding(0, 16),
    },
  },
  settingItem: {
    ...mixins.padding(12, 0),
    ...mixins.borderBottom(1, 'solid', mixins.globalBorderColor),
    ':last-child': {
      borderBottom: 'none',
    },
    [mediaBreakpoints.mobile]: {
      ...mixins.padding(24, 16),
      borderBottom: 'none',
      border: `1px solid ${colors.catalystLight30}`,
      borderRadius: 4,
      ...mixins.margin(16, 0),
      ':last-child': {
        marginBottom: 0,
      },
    },
  },
  settingItemTitle: {
    fontSize: 14,
    display: 'block',
    textAlign: 'center',
    ...mixins.margin(0, 0, 24),
    ...mixins.padding(0),
    [mediaBreakpoints.mobile]: {
      fontSize: 16,
    },
  },
  mobileExitButton: {
    ...mixins.margin(16, 0, 26),
  },
});

const deviceSettingItems = [
  {
    display: 'Video',
    // when device selected changes for the input, new device will be set to local storage for twilio,
    // we are not submitting the device id to the backend so passing noop onChange here.
    component: <VideoInputDevice name="video" onChange={noop} data-it="VideoInputDevice" />,
  },
  {
    display: 'Captions',
    component: <ClosedCaptionsToggle />,
  },
  {
    display: 'Microphone',
    component: <AudioInputDevice name="microphone" sidebarView onChange={noop} data-it="AudioInputDevice" />,
  },
  {
    display: 'Speakers',
    component: <AudioOutputDevice name="speaker" sidebarView onChange={noop} data-it="AudioOutputDevice" />,
  },
];

export const DeviceSetting: React.FC<DeviceSetting.Props> = () => {
  const { roomId } = useRoom();
  const { closedCaptions } = useVideoContext();
  const { isMobileView } = useRootSelector(state => state.layout);
  const dispatch = useDispatch();

  const onExitButtonClick = () => {
    updateExitModalStatus(true)(dispatch);
  };

  const videoAuthToken = getVideoVendorApiKey(roomId);
  return (
    <div className={css(styles.deviceSetting)} data-dd-privacy="allow">
      <LocalRoomSetup videoAuthToken={videoAuthToken || ''} />
      <div className={css(styles.wrapper)}>
        {deviceSettingItems
          .filter(settingItem => {
            return settingItem.display === 'Captions' ? closedCaptions.isSupported : true;
          })
          .map((settingItem, i) => (
            <div className={css(styles.settingItem)} key={i}>
              <label className={css(styles.settingItemTitle)} htmlFor={settingItem.display}>
                {settingItem.display}
              </label>
              {settingItem.component}
            </div>
          ))}
        {isMobileView && (
          <ActionButton
            data-dd-privacy="allow"
            color={colors.catalyst}
            customStyle={styles.mobileExitButton}
            onClick={onExitButtonClick}
          >
            Exit video call
          </ActionButton>
        )}
      </div>
    </div>
  );
};
